import { defaultLocale, localizedSlugs, RouteKey, routeKeys, routing, sitemapStaticRouteKeys } from '@/i18n/routing';
import { TLangIso, TLocale } from '@/types/global';
import { Metadata, MetadataRoute } from 'next';
import { INDEX_PAGES, PUBLIC_BASE_URL } from './constants';
import { getBaseUrl } from './generic';

export interface IMetadata {
  title: string;
  description: string;
  isIndexed: boolean;
  images?: string[];
  lang: TLangIso;
  locale?: TLocale;
  imgSrc?: string;
  pageHref?: Record<TLocale, string>;
}

export const generateMetadataObj = ({
  title,
  description,
  isIndexed,
  lang,
  locale,
  imgSrc,
  pageHref
}: IMetadata): Metadata => {
  const pageLocale: TLocale = locale || lang.split('-')[0] as TLocale;
  const defaultTitle = {
    en: 'Agencia de Marketing Inmobiliario Creativo | BergaGonzález',
    es: 'Real Estate Marketing & Branding Agency | BergaGonzález',
  }

  const defaultDescription = {
    en: 'We build iconic real estate brands through 3D visuals and unique storytelling. Experts in architectural visualization and concept development.',
    es: 'Creamos marcas inmobiliarias memorables con visualización 3D y narrativas que emocionan. Especialistas en arquitectura y desarrollo de concepto.',
  }

  return {
    metadataBase: new URL(getBaseUrl() || ''),
    title: title || defaultTitle[pageLocale],
    description: description || defaultDescription[pageLocale],
    robots: {
      // cambiar a true por defecto al publicar la web:
      index: isIndexed,
      follow: isIndexed,
    },
    openGraph: {
      title: title || defaultTitle[pageLocale],
      description: description || defaultDescription[pageLocale],
      siteName: 'BergaGonzález',
      images: [
        imgSrc || `${getBaseUrl()}/logo-metadata.png`,
      ],
      type: 'website',
    },
    ...pageHref && { alternates: {
      canonical: pageHref[pageLocale],
      languages: {
        'en': pageHref.en,
        'es': pageHref.es,
        'x-default': pageHref[defaultLocale],
      }
    }}
  };
};

export const generateMetadataDefault = ({
  lang,
  // isIndexed,
}: {
  lang: TLangIso;
  isIndexed: boolean;
}): Metadata => {
  const title =
    lang === ('es-ES' as TLangIso) ? 'BergaGonzález' : 'BergaGonzalez';

  const description =
    lang === ('es-ES' as TLangIso)
      ? 'Default description in Spanish'
      : 'Default description in English';

  return {
    title: title,
    description: description,
    robots: {
      index: INDEX_PAGES,
      follow: INDEX_PAGES
    },
    openGraph: {
      title: title,
      description: description,
      siteName: 'BergaGonzalez',
      type: 'website',
      images: [`${process.env.NEXT_PUBLIC_ADMIN_URL}/logo-metadata.png`],
    },
  };
};

export const isHomePage = (path: string) => {
  const HOME_PATHS = ['/', '/en', '/es', '/preview/home', '/en/preview/home', '/es/preview/home'];
  return HOME_PATHS.includes(path);
};

export const isHomeOrPresentationSlug = (path: string) => {
  return isHomePage(path) || path.includes('presentation');
};

export const getRouteKeyFromSlug = (
  slug: string,
  locale: string
): RouteKey | undefined => {
  return Object.entries(localizedSlugs).find(
    ([, slugs]) => slugs[locale] === slug
  )?.[0] as RouteKey | undefined;
};

export function getTranslatedPathSlug(
  path: string,
  fromLocale: TLocale,
  toLocale: TLocale
): string | undefined {
  const routeKey = routeKeys.find(
    (key) => localizedSlugs[key][fromLocale] === path
  );
  return routeKey
    ? `${toLocale === 'es' ? '/es' : ''}/${localizedSlugs[routeKey][toLocale]}`
    : undefined;
}

export const slugIsKnown = (slug: string) => {
  return Object.values(localizedSlugs).some((slugs) =>
    Object.values(slugs).includes(slug)
  );
};

export type TUsedSlugLocales = {
  [key in TLocale]: string;
};

export const getUsedSlugLocales = (): TUsedSlugLocales => {
  return {
    en: routing.defaultLocale === 'en' ? '' : 'en',
    es: routing.defaultLocale === 'es' ? '' : 'es',
  };
};

export const getLocalesExcludingDefault = () => {
  const locales = getUsedSlugLocales();
  const defaultLocale = routing.defaultLocale;

  return Object.keys(locales).filter((locale) => locale !== defaultLocale);
};

export const getAlternateUrls = (slugs: { [locale in TLocale]: string }) => {
  const defaultLocale = routing.defaultLocale;

  // Define an object for alternate language URLs
  const alternates: { languages: { [key in TLocale]?: string } } = {
    languages: {},
  };

  // Get the locales using the function
  const locales = getUsedSlugLocales();

  // Loop through locales and create alternate URLs
  for (const locale in locales) {
    if (locale !== defaultLocale) {
      const typedLocale = locale as TLocale;

      // Only add to alternates if the slug for the current locale is truthy
      if (slugs[typedLocale]) {
        alternates.languages[typedLocale] =
          `${getBaseUrl()}/${
            locales[typedLocale] ? locales[typedLocale] + '/' : ''
          }${slugs[typedLocale]}`;
      }
    }
  }

  return alternates;
};

export const getDefaultUrl = (slugs: { [locale in TLocale]: string }) => {
  const locales = getUsedSlugLocales();
  const defaultLocale = routing.defaultLocale;

  return `${getBaseUrl()}/${locales[defaultLocale] + '/' }${slugs[defaultLocale]}`;
};

export const generateStaticSitemaps = (): MetadataRoute.Sitemap => {
  const defaultLocale = routing.defaultLocale;

  // Array to hold the sitemap objects for static pages
  const staticPages: MetadataRoute.Sitemap = [];

  // Get the list of locales excluding the default locale
  const localesExcludingDefault = getLocalesExcludingDefault();

  // Iterate over the route keys to generate static URLs for each page
  sitemapStaticRouteKeys.forEach((routeKey) => {
    const slugs = localizedSlugs[routeKey.routeKey];

    // Construct URL for the default locale (will not include a prefix for default locale)
    const defaultUrl = `${getBaseUrl()}/${slugs[defaultLocale]}`;

    // Initialize alternates object for each page
    const alternates: { languages: { [key in TLocale]?: string } } = {
      languages: {},
    };

    // Add alternate URLs for the non-default locales
    localesExcludingDefault.forEach((locale) => {
      alternates.languages[locale as TLocale] =
        `${getBaseUrl()}/${locale === 'es' ? 'es/' : ''}${slugs[locale as TLocale]}`;
    });

    // Add the default URL for this page along with its alternates
    staticPages.push({
      url: defaultUrl,
      lastModified: new Date().toISOString(),
      changeFrequency: routeKey.changeFrequency,
      priority: routeKey.priority,
      alternates: alternates,
    });
  });

  return staticPages;
};

export const getLocaleUrlPrefix = (locale: TLocale): string => {
  const locales = getUsedSlugLocales();
  return locales[locale] ? `/${locales[locale]}` : '';
};

/** Formats a string to be used as a URL param
 * @param {string} str - The string to format
 * @returns {string}   - The formatted string
 **/
export const getStringForUrlParam = (str: string): string => {
  return str.replace(/ /g, '-').toLowerCase();
};

export function createStaticParamsForRoutes(routeKeys: RouteKey[]) {
  const { locales } = routing;

  const params = [];

  for (const locale of locales) {
    for (const key of routeKeys) {
      const slugString = localizedSlugs[key][locale];
      const slug = slugString.split('/'); // split nested paths
      
      params.push({ locale, slug });
    }
  }

  return params;
}

export function getLocalesStaticParams() {
  return routing.locales.map((locale) => ({ locale }));
}

export const getCannonicalUrl = (path: string, locale: TLocale): string => {
  const locales = getUsedSlugLocales();
  const prefix = routing.defaultLocale === locale ? '' : `/${locales[locale]}`;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${PUBLIC_BASE_URL}${prefix}${normalizedPath}`;
};

export const isWorkPage = (pathname: string): boolean => {
  return (pathname === `/${localizedSlugs.work.en}` || pathname === `/${localizedSlugs.work.es}`) ||
    (pathname.startsWith(`/${localizedSlugs.work.en}/`) || pathname.startsWith(`/${localizedSlugs.work.es}/`));
};

export const isMainWorkPage = (pathname: string): boolean => {
  return (pathname === `/${localizedSlugs.work.en}` || pathname === `/${localizedSlugs.work.es}`);
};

export const isProjectPage = (pathname: string): boolean => {
  return pathname.startsWith(`/${localizedSlugs.projects.en}/`) || pathname.startsWith(`/${localizedSlugs.projects.es}/`);
}

export const getProxyedDirectusImageUrl = (imageId: string): string => {
  return `${getBaseUrl()}/media/${imageId}`;
}